<template>
  <div>
    <div class="w-full flex flex-col md:space-y-5 space-y-3.5">
      <div class="grid md:grid-cols-2 grid-cols-1 md:gap-5 gap-3.5">
        <BaseSelect
          :required="true"
          :title="$t('new.art_type')"
          :items="artTypes"
          :selected="slectedLabel"
          @select="selectArtType"
        >
          <template #item="{ item }">{{
            item.label[locale as Locale]
          }}</template>
        </BaseSelect>
        <BaseSelect
          :title="$t('new.select_creation_year')"
          position="top"
          height="sm:max-h-96 max-h-[80vh] overflow-y-auto"
          :items="yearOptions"
          :selected="store.newArtwork.creationYear"
          @select="selectCreationYear"
        >
          <template #item="{ item }">
            {{ item.label }}
          </template>
        </BaseSelect>
      </div>

      <div class="grid md:grid-cols-2 grid-cols-1 md:gap-5 gap-3.5">
        <BaseMultiSelect
          :loading="meta.fetching"
          @select="selectMediums"
          :items="mediums"
          :title="$t('new.select_mediums')"
          :selected-items="store.newArtwork.mediums"
          position="bottom"
          height="max-h-96 overflow-y-auto"
        >
          <template #item="{ item }">
            {{ item.label }}
          </template>
        </BaseMultiSelect>
        <BaseMultiSelect
          :loading="meta.fetching"
          @select="selectGenre"
          :items="genres"
          :title="$t('new.select_genres')"
          :selected-items="store.newArtwork.genres"
          position="bottom"
          height="max-h-96 overflow-y-auto"
        >
          <template #item="{ item }">
            {{ item.label }}
          </template>
        </BaseMultiSelect>
        <BaseMultiSelect
          v-if="store.newArtwork.artType !== 'book'"
          :loading="meta.fetching"
          @select="selectStyle"
          :items="styles"
          :title="$t('new.select_styles')"
          :selected-items="store.newArtwork.styles"
          position="bottom"
          height="max-h-96 overflow-y-auto"
        >
          <template #item="{ item }">
            {{ item.label }}
          </template>
        </BaseMultiSelect>
      </div>
      <div
        class="grid gap-5"
        :class="
          selectedArtType.is3D ? 'sm:grid-cols-3 grid-cols-2' : 'grid-cols-2'
        "
      >
        <BaseInput
          type="number"
          :min="0"
          :minlength="0"
          v-model.number="store.newArtwork.dimensions.height"
          :placeholder="$t('new.height')"
          :label="$t('new.height')"
          :hint="
            store.newArtwork.dimensions.height > 0
              ? ''
              : $t('validation.min_value')
          "
        />
        <BaseInput
          type="number"
          :min="0"
          :minlength="0"
          v-model.number="store.newArtwork.dimensions.width"
          :placeholder="$t('new.width')"
          :label="$t('new.width')"
          :hint="
            store.newArtwork.dimensions.width > 0
              ? ''
              : $t('validation.min_value')
          "
        />
        <BaseInput
          :min="0"
          :minlength="0"
          type="number"
          v-if="selectedArtType.is3D"
          v-model.number="store.newArtwork.dimensions.depth"
          :placeholder="$t('new.depth')"
          :label="$t('new.depth')"
          :hint="
            store.newArtwork.dimensions.depth > 0
              ? ''
              : $t('validation.min_value')
          "
        />
      </div>
      <div class=""></div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, onMounted } from "vue";
import { artTypes, type ArtTypeItem, type YearItem } from "~~/types/artwork";
import type { MetadataDTO, MetadataItem } from "~~/types/metadata";
import { useI18n } from "vue-i18n";
import type { Locale } from "~~/types/global";

const store = useNewArtworkStore();
const meta = useMetaStore();
const { locale } = useI18n();

const selectedArtType = computed(() => {
  const key = store.newArtwork?.artType;
  return artTypes.find((i) => i.key === key) || artTypes[0]!;
});

const slectedLabel = computed<string>(
  () => selectedArtType.value.label[locale.value as Locale],
);

const mediums = computed<MetadataItem[]>(() =>
  meta.meta.filter((item: MetadataItem) => item.field === "medium"),
);
const genres = computed<MetadataItem[]>(() =>
  meta.meta.filter((item: MetadataItem) => item.field === "genre"),
);
const styles = computed<MetadataItem[]>(() =>
  meta.meta.filter((item: MetadataItem) => item.field === "style"),
);

const yearOptions = computed(() => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: 100 }, (_, i) => {
    const year = currentYear - i;
    return {
      label: year.toString(),
      value: year,
    };
  });
});

const selectArtType = (artType: ArtTypeItem) => {
  store.resetArtType(artType.key);
};

const selectMediums = (medium: MetadataItem) => {
  const mediumItem: MetadataDTO = {
    key: medium.key,
    label: { ...medium.label },
  };

  store.selectMedium(mediumItem);
};

const selectCreationYear = (year: YearItem) => {
  store.setCreationYear(year.value);
};

const selectGenre = (medium: MetadataItem) => {
  const mediumItem: MetadataDTO = {
    key: medium.key,
    label: { ...medium.label },
  };

  store.selectGenre(mediumItem);
};

const selectStyle = (medium: MetadataItem) => {
  const mediumItem: MetadataDTO = {
    key: medium.key,
    label: { ...medium.label },
  };

  store.selectStyle(mediumItem);
};
onMounted(async () => {
  await meta.setArtType("painting");
});
</script>
